import { useQuery } from '@tanstack/react-query'
import { useWorkspace } from 'components/providers'
import { HttpsCallableResult } from 'firebase/functions'
import { FullMetadata } from 'firebase/storage'
import {
  getDocById,
  getFileMeta,
  getFileUrl,
  getImageFromSiteUrl,
  listAllFilesInPath,
  updateDoc,
  uploadFile,
} from 'utils'

const mediaQueryKey = {
  media: (mediaId?: string) => ['media', mediaId],
  storageUrl: (workspaceId?: string, filePath?: string) => ['storageUrl', workspaceId, filePath],
  storageMeta: (workspaceId?: string, filePath?: string) => ['storageMeta', workspaceId, filePath],
}

export function useMedia(id?: string) {
  const { data: media, ...rest } = useQuery({
    queryKey: mediaQueryKey.media(id),
    queryFn: () => getDocById('Media', id!),
    enabled: !!id,
  })
  return { media, ...rest }
}

export function useStorageUrl(filePath?: string, fallbackPath?: string) {
  const { workspace } = useWorkspace()

  const { data: url, ...rest } = useQuery({
    queryKey: mediaQueryKey.storageUrl(workspace?.id, filePath),
    queryFn: () =>
      getFileUrl(filePath!, workspace!.id).catch(e => {
        if (fallbackPath) return getFileUrl(fallbackPath, workspace!.id)
        throw e
      }),
    enabled: Boolean(workspace?.id && filePath),
  })
  return { url, ...rest }
}

export function useStorageUrlForWorkspace(workspaceId: string, filePath?: string) {
  const { data: url, ...rest } = useQuery({
    queryKey: mediaQueryKey.storageUrl(workspaceId, filePath),
    queryFn: () => getFileUrl(filePath!, workspaceId),
    enabled: Boolean(workspaceId && filePath),
  })
  return { url, ...rest }
}

export function useStorageMeta(filePath?: string) {
  const { workspace } = useWorkspace()
  const { data: meta, ...rest } = useQuery({
    queryKey: mediaQueryKey.storageMeta(workspace?.id, filePath),
    queryFn: () => getFileMeta(filePath!, workspace!.id),
    enabled: Boolean(workspace?.id && filePath),
  })
  return { meta, ...rest }
}

export async function createImgSrc(file: File): Promise<string> {
  return new Promise(res => {
    const reader = new FileReader()
    reader.onload = () => {
      const blob = new Blob([reader.result as string], { type: 'image/jpeg' })
      const url = window.URL.createObjectURL(blob)
      res(url)
    }
    reader.readAsArrayBuffer(file)
  })
}

export function processImageForStorage(imageData: string) {
  //@ts-ignore
  const imageBuffer = imageData.imageBuffer
  const binaryString = atob(imageBuffer)
  const binaryLen = binaryString.length
  const bytes = new Uint8Array(binaryLen)
  for (let i = 0; i < binaryLen; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  //@ts-ignore
  const contentType = imageData.imageType

  if (!contentType) {
    throw new Error('Content type is null')
  }
  const blob = new Blob([bytes], { type: contentType })
  const fileExtension = contentType.split('/')[1]
  const file = new File([blob], `image.${fileExtension}`, { type: contentType })

  return file
}

export async function getImageFromUrl(siteUrl: string) {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Request timed out')), 3000)
  )

  try {
    const imageResponse = (await Promise.race([
      getImageFromSiteUrl({ url: siteUrl }),
      timeout,
    ])) as HttpsCallableResult<string>

    const imageData = imageResponse.data
    const file = processImageForStorage(imageData)

    return file
  } catch (error) {
    return undefined
  }
}

export async function directUploadImageToStorage(
  userId: string,
  siteUrl: string,
  filePath: string,
  eventId: string
) {
  const file = await getImageFromUrl(siteUrl)
  if (file) {
    await uploadFile(filePath, file)
    await updateDoc(userId, 'events', { id: eventId, hasImage: true })
  }
}

export async function getStorageUrlsInPath(
  path: string,
  workspaceId?: string
): Promise<{ path: string; url: string; meta: FullMetadata }[]> {
  const files = await listAllFilesInPath(path, workspaceId)
  const metaData = await Promise.all(
    files.map(f => {
      const url = getFileUrl(f)
      const meta = getFileMeta(f)
      return { url, meta }
    })
  )
  return await Promise.all(
    files.map(async (filePath, i) => ({
      path: filePath,
      url: await metaData[i].url,
      meta: await metaData[i].meta,
    }))
  )
}
